import {areSetsEqual, clamp} from '@benchling/js-utils';
import moment from 'moment';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SAMPLE_RESTRICTION_STATUS_LABELS, SampleRestrictionStatus} from 'constants/SampleStatusConstants';
import {SequenceSummary} from 'constants/SequenceConstants';
import {Status as ComputedFieldStatus} from 'constants/StatusConstants';
import {EntitySummary} from 'constants/UserConstants';
import {
  BioentityInfo,
  BioentitySummary,
  ContainingSchemaTagType,
  isSchemaFieldDatabaseBacked,
  MinimalSchemaTag,
  SchemaFieldDataType,
  SchemaTag,
  UnaffiliatedSchemaTag,
} from 'folder-items/FileConstants';
import {getRestrictedSampleUsersHeaderText} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import {PlateLayoutType, readablePlateLayout} from 'lab-automation/settings/RunSchemaSpecConstants';
import {
  LookupConfigSpec,
  LookupStepSpec,
  LookupStepType,
  RIGHT_ARROW,
} from 'lookup-config/LookupConfigModalConstants';
import * as DatetimeFields from 'notebook/data-tables/DatetimeFields';
import * as ExcessUtil from 'notebook/tables/excess/Util';
import {
  ServerResolutionResult,
  StringResolutionType,
  StructuredTableCoerceTypedValueToTypeQuery,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {
  ArrayTypedValue,
  BaseTypedValueType,
  Cell,
  CELL_FORMAT_PROPERTIES,
  TypedValue,
  TypedValueType,
} from 'notebook/tables/TableConstants';
import {convertStorablePlaceholdersTypedValueToString} from 'notebook/tables/typed-values/storables/StorablePlaceholdersTypedValue';
import {type ResolvedValueInfo} from 'notebook/tables/typed-values/TypedValueResolution';
import {
  BatchInfo,
  BioentityLinkSchemaFieldTypedValue,
  BioentityTypedValue,
  BooleanSchemaFieldTypedValue,
  BooleanTypedValue,
  FloatSchemaFieldTypedValue,
  IntegerSchemaFieldTypedValue,
  LongTextSchemaFieldTypedValue,
  NumberTypedValue,
  PartiesTypedValue,
  PartyInfo,
  PlateLayoutTypedValue,
  SampleRestrictionStatusTypedValue,
  ScientificTypedValue,
  SelectorSchemaFieldTypedValue,
  StorableInfo,
  StorablesTypedValue,
  StringTypedValue,
  TaskStatusTypedValue,
  TextSchemaFieldTypedValue,
  TypedValueLinkedValueWithNullishId,
  TypedValueTargetBlob,
  TypedValueTargetEntry,
  TypedValueTargetFileGuaranteedId,
  TypedValueTargetFileNullishId,
  TypedValueTargetStorable,
  UnitReferenceTypedValue,
  UserTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import {toConditionReplicateString} from 'procedures/utils/ProcedureConditionReplicates';
import {getStore} from 'redux-stores/Store';
import {FULFILLMENT_STATUS_NAMES} from 'requests/fulfillments/FulfillmentConstants';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import {formatGridCoordinates} from 'samples/SampleUtil';
import {FIELD_TYPES, FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {BenchlingBlob, BlobUploadStatus} from 'schemas/tags/BlobConstants';
import {COMPUTING_STATUS_TEXT} from 'schemas/tags/computed/ComputedTagUtil';
import {fieldTypeSupportsLegalText, schemaTagHasLegalText} from 'schemas/tags/LegalTextSelectors';
import {getLinksKeyFromFieldType, getTagUpdateParams} from 'schemas/tags/TagUtil';
import APIUtil from 'util/APIUtil';
import {asyncTryParse, asyncTryParseDate, asyncTryParseDatetimeAsDate} from 'util/AsyncDateUtilWithTimezone';
import {CANONICAL_FORMAT} from 'util/DateConstants';
import {formalTime, tryParseDate} from 'util/DateUtil';
import {decimalRoundingStrategyToDecimalJSRoundingMode} from 'util/Decimals';
import * as logging from 'util/Logging';
import pick from 'util/pick';
import prettyFloat from 'util/prettyFloat';
import {assertNever, Optional} from 'util/Types';

import {LookupConfigInfoTypedValue} from './TypedValueTypes';

export const EMPTY_TYPED_VALUE: {type: BaseTypedValueType.STRING; rawValue: string} = {
  type: BaseTypedValueType.STRING,
  rawValue: '',
};

export type AnyTypedValueLinkedObject =
  | TypedValueLinkedValueWithNullishId
  | TypedValueTargetFileNullishId
  | TypedValueTargetFileGuaranteedId
  | TypedValueTargetStorable
  | TypedValueTargetEntry
  | TypedValueTargetBlob
  | BatchInfo
  | StorableInfo
  | PartyInfo;

export function canParseTypedValueFromString(fieldType: FieldType): boolean {
  return [
    FIELD_TYPES.DATE.type,
    FIELD_TYPES.DATETIME.type,
    FIELD_TYPES.FLOAT.type,
    FIELD_TYPES.INTEGER.type,
    FIELD_TYPES.TEXT.type,
    FIELD_TYPES.LONG_TEXT.type,
    FIELD_TYPES.BOOLEAN.type,
    FIELD_TYPES.JSON.type,
  ].includes(fieldType);
}

export function isString(typedValue: TypedValue): boolean {
  switch (typedValue.type) {
    case FIELD_TYPES.TEXT.type:
    case FIELD_TYPES.LONG_TEXT.type:
    case ExcessUtil.VALUE_TYPES.STRING:
      return true;
    default:
      return false;
  }
}

export function isNumeric(typedValue: TypedValue): boolean {
  switch (typedValue.type) {
    case FIELD_TYPES.FLOAT.type:
    case FIELD_TYPES.INTEGER.type:
    case ExcessUtil.VALUE_TYPES.NUMBER:
    case ExcessUtil.VALUE_TYPES.SCIENTIFIC:
      return true;
    default:
      return false;
  }
}

export function isBaseNumericValueType(
  typedValue: TypedValue
): typedValue is NumberTypedValue | ScientificTypedValue {
  return (ExcessUtil.NUMERIC_TYPES as Array<string>).includes(typedValue.type);
}

export function isBaseNumericValueTypeArray(
  typedValues: Array<StringTypedValue | ScientificTypedValue | NumberTypedValue | BooleanTypedValue | null>
): typedValues is Array<NumberTypedValue | ScientificTypedValue> {
  return typedValues.every((typedValue) => typedValue && isBaseNumericValueType(typedValue));
}

export function isFloatSchemaFieldType(typedValue: TypedValue): typedValue is FloatSchemaFieldTypedValue {
  return typedValue.type === FIELD_TYPES.FLOAT.type;
}

export function isBaseBoolean(typedValue: TypedValue): typedValue is BooleanTypedValue {
  return typedValue.type === BaseTypedValueType.BOOLEAN;
}

export function getBaseCell(cellValue: Cell): Partial<Cell> {
  return pick(cellValue, ...CELL_FORMAT_PROPERTIES);
}

export function isLookupConfigInfoType(typedValue: TypedValue): typedValue is LookupConfigInfoTypedValue {
  return typedValue.type === BaseTypedValueType.LOOKUP_CONFIG_INFO;
}

export function isPlateLayoutColumnType(typedValue: TypedValue): typedValue is PlateLayoutTypedValue {
  return typedValue.type === BaseTypedValueType.PLATE_LAYOUT;
}

export function isUnitReferenceTypedValue(
  typedValue: TypedValue | null | undefined
): typedValue is UnitReferenceTypedValue {
  return typedValue?.type === BaseTypedValueType.UNIT_REFERENCE;
}

export function sanitizeBenchlingBlobs(blobs: Array<BenchlingBlob>): Array<BenchlingBlob> {
  return blobs.map((blob) => ({
    id: blob.id,
    name: blob.name,
    url: blob.url,
    link_type: blob.link_type,
    previewFile: blob.previewFile,
  }));
}

const MAP_BLOB_UPLOAD_STATUS: Record<
  Exclude<TypedValueTargetBlob['uploadStatus'], undefined>,
  BlobUploadStatus
> = {
  ABORTED: BlobUploadStatus.ABORTED,
  COMPLETE: BlobUploadStatus.COMPLETE,
  IN_PROGRESS: BlobUploadStatus.IN_PROGRESS,
};

export function convertTypedValueBlob(blob: TypedValueTargetBlob): BenchlingBlob {
  return {...blob, uploadStatus: blob.uploadStatus ? MAP_BLOB_UPLOAD_STATUS[blob.uploadStatus] : undefined};
}

/**
 * Given a tag, convert it to a value that can be used in Excess.
 *
 * Used for LOOKUPFIELD which stores tags in its externalCache.
 * Also used to convert stored results into rows to add them to tables.
 */
export function convertTagToTypedValue(
  tag: SchemaTag | UnaffiliatedSchemaTag | MinimalSchemaTag
): TypedValue {
  const ComputedTagUtil = require('schemas/tags/computed/ComputedTagUtil');

  const type = schemaTagHasLegalText(tag) ? FieldType.SELECTOR : tag.tagSchemaField.fieldType;

  if (ComputedTagUtil.shouldShowComputedState(tag)) {
    const computedState = tag.computedState!;
    return {
      type: 'COMPUTING',
      value: '',
      computedState: {
        status: computedState.status,
        // eslint-disable-next-line benchlint/no-in-operator
        errorMessage: 'errorMessage' in computedState ? computedState.errorMessage : null,
      },
    };
  }
  let typedValue: TypedValue = {
    type,
    value: tag.value === undefined ? null : tag.value,
    // url is only used in legacy cases, so it shouldn't be included in the typed value format.
    // eslint-disable-next-line no-restricted-syntax
    ..._.omit(getTagUpdateParams(tag), 'url'),
  };

  // Float tags have a rawValue field that contains the original user input value (the value
  // field may be rounded).
  if (type === FieldType.FLOAT) {
    typedValue = {...typedValue, rawValue: tag.rawValue} as FloatSchemaFieldTypedValue;
  }

  // TODO(BNCH-12786): For date tags, getTagUpdateParams returns a `value` key.
  //
  // But date typed values are expected to have a `dateValue` key,
  // so we need to add the `dateValue` key manually.
  //
  // Benefits of including a `dateValue` key:
  // - Fixes `coerceToPrimitive` to return the date value rather than null.
  // = Makes `convertTagToTypedValue` consistent with its backend equivalent, `field_value_to_cell_value`.
  if (typedValue.type === FieldType.DATE) {
    typedValue = {...typedValue, dateValue: typedValue.value != null ? tryParseDate(typedValue.value) : null};
  }

  // For JSON tags the tag value is "null" when jsonValue is `null` / unset.
  if (typedValue.type === FieldType.JSON) {
    if (typedValue.jsonValue == null) {
      typedValue = {...typedValue, value: null};
    }
  }

  // Blob types may need to be sanitized to remove certain properties
  if (typedValue.type === FieldType.BLOB_LINK) {
    typedValue = {
      ...typedValue,
      targetBlobs: sanitizeBenchlingBlobs(
        (typedValue.targetBlobs ?? []).map((blob) => convertTypedValueBlob(blob))
      ),
    };
  }

  return typedValue;
}

/**
 * Interpret this typedValue as an instance of this tagSchemaField. If the typedValue does not
 * match, this method returns null and the caller needs a fallback.
 *
 * Note that the tag here is enough to satisfy the tag propType, but does not have an ID and is not
 * guaranteed to have a value.
 */

export function convertTypedValueToTag<T extends SchemaField | SchemaFieldDataType>(
  typedValue: TypedValue,
  tagSchemaField: T
): ContainingSchemaTagType<T> | null {
  if (isSchemaFieldDatabaseBacked(tagSchemaField)) {
    const hasLegalText =
      fieldTypeSupportsLegalText(tagSchemaField.fieldType) && typedValue.type === FIELD_TYPES.SELECTOR.type;
    if (typedValue.type === BaseTypedValueType.COMPUTING) {
      return {
        tagSchemaField,
        name: tagSchemaField.name,
        // eslint-disable-next-line no-restricted-syntax
        ..._.omit(typedValue, 'type'),
      };
    }
    if (typedValue.type !== tagSchemaField.fieldType && !hasLegalText) {
      return null;
    }
    // TextFieldType needs a URL for legacy reasons, so just add an empty one. See TextSchemaTag on the
    // backend, which is the representation for TextFieldType. In the future, this should be changed to
    // be no longer necessary.
    const maybeURL = typedValue.type === FIELD_TYPES.TEXT.type ? {url: ''} : {};
    return {
      tagSchemaField,
      name: tagSchemaField.name,
      ...maybeURL,
      // eslint-disable-next-line no-restricted-syntax
      ..._.omit(typedValue, 'type'),
      ...(tagSchemaField.isComputed && {
        computedState: {status: ComputedFieldStatus.SUCCEEDED, errorMessage: null},
      }),
    };
  } else {
    // The type is correct, and callers will infer "Unaffiliated" properly
    return convertTypedValueToUnaffiliatedTag(typedValue, tagSchemaField) as ContainingSchemaTagType<T>;
  }
}

export function convertTypedValueToUnaffiliatedTag(
  typedValue: TypedValue,
  tagSchemaField: SchemaFieldDataType
): UnaffiliatedSchemaTag | null {
  const hasLegalText =
    fieldTypeSupportsLegalText(tagSchemaField.fieldType) && typedValue.type === FIELD_TYPES.SELECTOR.type;
  if (typedValue.type === BaseTypedValueType.COMPUTING) {
    return {
      tagSchemaField,
      // eslint-disable-next-line no-restricted-syntax
      ..._.omit(typedValue, 'type'),
    };
  }
  if (typedValue.type !== tagSchemaField.fieldType && !hasLegalText) {
    return null;
  }
  // TextFieldType needs a URL for legacy reasons, so just add an empty one. See TextSchemaTag on the
  // backend, which is the representation for TextFieldType. In the future, this should be changed to
  // be no longer necessary.
  const maybeURL = typedValue.type === FIELD_TYPES.TEXT.type ? {url: ''} : {};
  return {
    tagSchemaField,
    ...maybeURL,
    // eslint-disable-next-line no-restricted-syntax
    ..._.omit(typedValue, 'type'),
  };
}

/**
 * Parse a float for an ELN table.
 */
export function parseTextAsFloat(text: string): number | null {
  const parsedValue = ExcessUtil.parseValue(text);
  if (isBaseNumericValueType(parsedValue)) {
    return parsedValue.rawValue;
  }
  return null;
}

/**
 * Parse an integer for an ELN table. Allows scientific notation.
 */
export function parseTextAsInteger(text: string): number | null {
  const floatValue = parseTextAsFloat(text);
  return floatValue != null && Number.isInteger(floatValue) ? floatValue : null;
}

async function resolveDateQuery(
  query: StructuredTableCoerceTypedValueToTypeQuery
): Promise<{resolvedValue: TypedValue; errorMessage: string | null}> {
  if (query.fieldType !== FieldType.DATE) {
    return {resolvedValue: query.typedValue, errorMessage: null};
  }
  const text = formatTypedValue(query.typedValue);
  const dateValue = await asyncTryParseDate(text, query.languageTag, query.timezoneName);
  const hasValidDate = dateValue != null;
  const datetimeValueAsDate = hasValidDate
    ? null
    : await asyncTryParseDatetimeAsDate(text, query.languageTag, query.timezoneName);
  let resolvedValue;
  if (hasValidDate) {
    resolvedValue = {type: query.fieldType, value: text, dateValue};
  } else {
    resolvedValue = {
      type: query.fieldType,
      value: datetimeValueAsDate ?? text,
      dateValue: datetimeValueAsDate,
    };
  }
  return {
    resolvedValue,
    errorMessage:
      text && !hasValidDate && datetimeValueAsDate == null ? `${text} is not a valid date.` : null,
  };
}

async function resolveDatetimeQuery(
  query: StructuredTableCoerceTypedValueToTypeQuery
): Promise<{resolvedValue: TypedValue; errorMessage: string | null}> {
  if (query.fieldType !== FieldType.DATETIME) {
    return {resolvedValue: query.typedValue, errorMessage: null};
  }
  const text = formatTypedValue(query.typedValue);
  const datetimeValue = await asyncTryParse(text, query.languageTag, query.timezoneName);
  return {
    resolvedValue: {type: query.fieldType, value: text, datetimeValue},
    errorMessage: text && datetimeValue == null ? `${text} is not a valid datetime.` : null,
  };
}

export async function coerceTypedValueToTypeQueryResolution(
  query: StructuredTableCoerceTypedValueToTypeQuery
): Promise<ServerResolutionResult> {
  let resolvedValue;
  let errorMessage;

  switch (query.fieldType) {
    case FieldType.DATE: {
      const resolution = await resolveDateQuery(query);
      resolvedValue = resolution.resolvedValue;
      errorMessage = resolution.errorMessage;
      break;
    }
    case FieldType.DATETIME: {
      const resolution = await resolveDatetimeQuery(query);
      resolvedValue = resolution.resolvedValue;
      errorMessage = resolution.errorMessage;
      break;
    }
    default:
      resolvedValue = query.typedValue;
      errorMessage = null;
  }

  return errorMessage
    ? {type: undefined, error: errorMessage, resolvedValue: undefined}
    : {type: StringResolutionType.CUSTOM, error: undefined, resolvedValue: {typedValue: resolvedValue}};
}

/**
 * Return the appropriate Excess error for a parsed numeric value, or null if there is no error.
 *
 * @param value - parsed numeric value from inputted text
 * @param text - user-inputted text
 * @param {min, max} - optional numerical thresholds for value
 */
function getExcessErrorForNumber(
  value: number | null,
  text: string | null,
  fieldType: FieldType,
  {min, max}: {min?: number | null; max?: number | null} = {}
): ExcessUtil.ValueError | null {
  if (value == null) {
    if (text == null || text === '') {
      return null;
    }

    return new ExcessUtil.ValueError(
      `${text} is not a valid ${fieldType === FieldType.INTEGER ? 'integer' : 'number'}.`
    );
  }
  if (min != null && value < min) {
    return new ExcessUtil.ValueError(`Below data threshold: ${min}`);
  } else if (max != null && value > max) {
    return new ExcessUtil.ValueError(`Above data threshold: ${max}`);
  }
  return null;
}

/**
 * Converts an arbitrary TypedValue into a BooleanTypedValue if the typed value contains boolean-like text.
 * Otherwise, returns the original TypedValue and an error if the value is not empty.
 */
export function resolveTypedValueToBoolean(typedValue: TypedValue): ResolvedValueInfo {
  const formattedValue = formatTypedValue(typedValue);
  const booleanMatches = new RegExp(`^${ExcessUtil.BOOLEAN_REGEX.source}`, 'i').exec(formattedValue);
  if (!booleanMatches) {
    return {
      resolvedValue: typedValue,
      error:
        formattedValue != null && formattedValue !== ''
          ? new ExcessUtil.ValueError(`${formattedValue} is not a valid boolean.`)
          : null,
    };
  }
  return {
    resolvedValue: {type: BaseTypedValueType.BOOLEAN, booleanValue: booleanMatches[1] != null},
    error: null,
  };
}

/**
 * Called during typed value resolution. The output of this function will likely be persisted in the
 * evaluation snapshot of an entry/document.
 */
export function coerceTypedValueToType(
  typedValue: TypedValue,
  fieldType: FieldType,
  languageTag: string | null,
  {
    numericMin,
    numericMax,
    schemaDecimalPrecision,
  }: {numericMin?: number | null; numericMax?: number | null; schemaDecimalPrecision?: number | null} = {}
): {resolvedValue: TypedValue; error: ExcessUtil.ValueError | null} {
  switch (fieldType) {
    case FieldType.DATE: {
      const text = formatTypedValue(typedValue);
      const dateValue = tryParseDate(text, languageTag);
      const hasValidDate = dateValue != null;
      const datetimeValueAsDate = hasValidDate
        ? null
        : DatetimeFields.tryParseDatetimeAsDate(text, languageTag);
      return {
        resolvedValue: {
          type: fieldType,
          value: datetimeValueAsDate ?? text,
          dateValue: dateValue ?? datetimeValueAsDate,
        },
        error:
          text && !hasValidDate && datetimeValueAsDate == null
            ? new ExcessUtil.ValueError(`${text} is not a valid date.`)
            : null,
      };
    }
    case FieldType.DATETIME: {
      const text = formatTypedValue(typedValue);
      const datetimeValue = DatetimeFields.tryParse(text, languageTag);
      return {
        resolvedValue: {type: fieldType, value: text, datetimeValue},
        error:
          text && datetimeValue == null
            ? new ExcessUtil.ValueError(`${text} is not a valid datetime.`)
            : null,
      };
    }
    case FieldType.FLOAT: {
      if (isFloatSchemaFieldType(typedValue)) {
        // ft_float typed values may differ in their display precision
        return {
          resolvedValue: {
            ...typedValue,
            value:
              schemaDecimalPrecision != null && typedValue.floatValue != null
                ? prettyFloat(
                    typedValue.floatValue,
                    schemaDecimalPrecision,
                    decimalRoundingStrategyToDecimalJSRoundingMode(
                      BenchlingConfig.SCHEMAS_DECIMAL_ROUNDING_STRATEGY
                    )
                  )
                : typedValue.value,
          },
          error: null,
        };
      }

      if (isBaseNumericValueType(typedValue)) {
        const resolvedValue = resolveBaseNumericTypedValueToFloatSchemaTypedValue(
          typedValue,
          schemaDecimalPrecision
        );
        return {
          resolvedValue,
          error: getExcessErrorForNumber(resolvedValue.floatValue, resolvedValue.value, fieldType, {
            min: numericMin,
            max: numericMax,
          }),
        };
      }

      const text = formatTypedValue(typedValue);
      const floatValue = parseTextAsFloat(text);
      return {
        resolvedValue: {
          type: fieldType,
          value:
            schemaDecimalPrecision != null && floatValue != null
              ? floatValue.toFixed(schemaDecimalPrecision)
              : text,
          rawValue: text,
          floatValue,
        },
        error: getExcessErrorForNumber(floatValue, text, fieldType, {
          min: numericMin,
          max: numericMax,
        }),
      };
    }
    case FieldType.INTEGER: {
      const text = formatTypedValue(typedValue);
      const integerValue = parseTextAsInteger(text);
      return {
        resolvedValue: {type: fieldType, value: text, integerValue},
        error: getExcessErrorForNumber(integerValue, text, fieldType, {min: numericMin, max: numericMax}),
      };
    }
    case FieldType.JSON: {
      const text = formatTypedValue(typedValue) || '';
      let jsonValue = null;
      let error = null;
      try {
        jsonValue = text ? JSON.parse(text) : null;
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch {
        error = new ExcessUtil.ValueError('Invalid JSON');
      }
      return {resolvedValue: {type: fieldType, value: text, jsonValue}, error};
    }
    case FieldType.TEXT:
    case FieldType.LONG_TEXT: {
      const text = formatTypedValue(typedValue);
      return {
        resolvedValue:
          // This is gross but necessary due to TS currently being unable to properly deduce the
          // precise subset of remaining valid options from a typed union (in this case, TypedValue).
          fieldType === FieldType.TEXT
            ? {type: FieldType.TEXT, value: text}
            : {type: FieldType.LONG_TEXT, value: text},
        error: null,
      };
    }
    case FieldType.BOOLEAN: {
      if (!isTypedValueEmpty(typedValue)) {
        const resolvedValueInfo = resolveTypedValueToBoolean(typedValue);
        if (isBaseBoolean(resolvedValueInfo.resolvedValue)) {
          resolvedValueInfo.resolvedValue = {
            type: FieldType.BOOLEAN,
            booleanValue: resolvedValueInfo.resolvedValue.booleanValue,
            value: formatTypedValue(typedValue),
          } satisfies BooleanSchemaFieldTypedValue;
        }
        return resolvedValueInfo;
      }
      return {resolvedValue: typedValue, error: null};
    }
    default:
      return {resolvedValue: typedValue, error: null};
  }
}

function _parseDate(dateValue: string | null | undefined, languageTag?: string | null): string | null {
  if (dateValue == null) {
    return dateValue ?? null;
  }
  return tryParseDate(dateValue, languageTag ?? undefined);
}

function _parseDatetime(
  datetimeValue: string | null | undefined,
  languageTag?: string | null
): string | null {
  if (datetimeValue == null) {
    return datetimeValue ?? null;
  }
  return tryParseDate(datetimeValue, languageTag ?? undefined, true, CANONICAL_FORMAT);
}

export type EvaluablePrimitive =
  | string
  | number
  | boolean
  | Array<boolean>
  | Array<string>
  | Array<number>
  | null
  | undefined;

/**
 * Extracts a JS primitive for Excess calculations.
 *
 * @param typedValue The TypedValue to extract from
 * @param param1 Extra options for primitive coercion
 *  - shouldCoerceStringToNumber: Uses the Excess Evaluator to convert the string primitive to a number primitive
 *  - shouldCoerceDateToNumber: Uses the Excess Evaluator to convert dates and datetimes to a number primitive
 *  - shouldUseIds: Uses the ids of targets instead of their names for LinkTypedValues, Bioentities, and Storables
 * @returns An EvaluablePrimitive - i.e. a string, number, boolean, array of strings, array of numbers, or null
 *
 * NOTE: Edits to this function must be reflected in
 * benchling/notebook/structured_tables/export/typed_value.py:coerce_to_primitive.
 */
export function coerceToPrimitive(
  typedValue: TypedValue,
  {
    shouldCoerceStringToNumber = false,
    shouldCoerceDateToNumber = false,
    shouldUseIds = false,
    languageTag,
  }: {
    shouldCoerceStringToNumber?: boolean;
    shouldCoerceDateToNumber?: boolean;
    shouldUseIds?: boolean;
    languageTag?: string | null;
  } = {}
): EvaluablePrimitive {
  let primitive: EvaluablePrimitive;

  switch (typedValue.type) {
    case 'ft_float':
      primitive = typedValue.floatValue;
      break;
    case 'ft_integer':
      primitive = typedValue.integerValue;
      break;
    case 'ft_string':
    case 'ft_long_text':
      primitive = typedValue.value;
      break;
    case FieldType.SELECTOR:
      primitive = typedValue.value;
      break;
    case 'ft_blob_link':
    case 'ft_entry_link':
    case 'ft_batch_link':
    case 'ft_file_link':
    case 'ft_storable_link': {
      const targets = getLinksFromTypedValue(typedValue) as Array<TypedValueTargetStorable>;
      if (!targets) {
        primitive = typedValue.value;
        break;
      }
      primitive = targets.map((target) => (shouldUseIds ? target.id : target.name));
      if (primitive.length === 1) {
        primitive = primitive[0];
      }
      break;
    }
    case 'ft_date':
      if (shouldCoerceDateToNumber && typedValue.dateValue) {
        const value = DatetimeFields.tryParseAsMoment(typedValue.dateValue, languageTag ?? undefined);
        if (value) {
          primitive = ExcessUtil.momentToDaysSinceEpoch(value);
          break;
        }
      }
      primitive = _parseDate(typedValue.dateValue, languageTag);
      break;
    case 'ft_datetime':
      if (shouldCoerceDateToNumber && typedValue.datetimeValue) {
        const value = DatetimeFields.tryParseAsMoment(typedValue.datetimeValue, languageTag ?? undefined);
        if (value) {
          primitive = ExcessUtil.momentToDaysSinceEpoch(value);
          break;
        }
      }
      primitive = _parseDatetime(typedValue.datetimeValue, languageTag);
      break;
    case 'JSON':
    case 'ft_json':
      primitive = typedValue.jsonValue != null ? JSON.stringify(typedValue.jsonValue) : null;
      break;
    case 'STRING':
    case 'NUMBER':
    case 'SCIENTIFIC':
      primitive = typedValue.rawValue;
      break;
    case FieldType.BOOLEAN:
    case 'ft_boolean':
    case 'BOOLEAN':
      primitive = typedValue.booleanValue;
      break;
    case 'BIOENTITY':
      primitive = shouldUseIds ? typedValue.bioentitySummary.id : typedValue.bioentitySummary.name;
      break;
    case 'DATE':
      if (shouldCoerceDateToNumber && typedValue.value) {
        const value = DatetimeFields.tryParseAsMoment(typedValue.value, languageTag ?? undefined);
        if (value) {
          primitive = ExcessUtil.momentToDaysSinceEpoch(value);
          break;
        }
      }
      return _parseDate(typedValue.value, languageTag);
    case 'DATETIME':
      if (shouldCoerceDateToNumber && typedValue.value) {
        const value = DatetimeFields.tryParseAsMoment(typedValue.value, languageTag ?? undefined);
        if (value) {
          primitive = ExcessUtil.momentToDaysSinceEpoch(value);
          break;
        }
      }
      return _parseDatetime(typedValue.value, languageTag);
    case 'CLIENT_IDENTIFIABLE':
      return typedValue.name;
    case 'PARTIES':
      primitive = typedValue.partySummaries.map((partySummary) => partySummary.handle);
      break;
    case 'SAMPLE_RESTRICTION_STATUS':
      primitive = typedValue.status;
      break;
    case 'USER':
      return typedValue.userHandle;
    case 'TASK_STATUS':
      return typedValue.displayName;
    case 'WORKFLOW_OUTPUT':
    case 'WORKFLOW_TASK':
      return shouldUseIds ? typedValue.id : typedValue.humanId;
    case 'ARRAY':
      // TODO(BNCH-108653): Fix these two recursive calls. An array of EvaluablePrimitives is not
      // itself a valid EvaluablePrimitive
      primitive = typedValue.rawValue.map((value) =>
        coerceToPrimitive(value, {
          shouldCoerceStringToNumber,
          shouldCoerceDateToNumber,
          shouldUseIds,
          languageTag,
        })
      ) as EvaluablePrimitive;
      break;
    case BaseTypedValueType.ARRAY:
      // ArrayTypedValue is slightly different than FinalArrayTypedValue so we handle it separately
      primitive = typedValue.rawValue.map((value) =>
        coerceToPrimitive(value, {
          shouldCoerceStringToNumber,
          shouldCoerceDateToNumber,
          shouldUseIds,
          languageTag,
        })
      ) as EvaluablePrimitive;
      break;
    case 'STORABLES':
      primitive = typedValue.storableSummaries.map((storableSummary) =>
        shouldUseIds ? storableSummary.id : storableSummary.name
      );
      if (primitive.length === 1) {
        primitive = primitive[0];
      }
      break;
    case 'STORABLE_PLACEHOLDERS':
      primitive = typedValue.storablePlaceholders
        .filter((storableInfo): storableInfo is StorableInfo => storableInfo != null)
        .map((storableInfo) => (shouldUseIds ? storableInfo.id : storableInfo.name));
      if (primitive.length === 1) {
        primitive = primitive[0];
      }
      break;
    case 'GRID_COORDINATES':
      primitive = [typedValue.gridCoordinates.rowIndex, typedValue.gridCoordinates.columnIndex];
      break;
    case 'ft_selector':
    case 'ft_part_link':
    case 'ft_transcription_link':
    case 'ft_translation_link':
    case 'ft_aa_part_link':
    case 'ft_assay_run_link':
    case 'ft_assay_request_link':
    case 'ft_assay_result_link':
    case 'BATCH':
    case 'COMPUTING':
    case 'ENTRY':
    case 'FULFILLABLE_STATUS':
    case 'LOOKUP_CONFIG_INFO':
    case 'PLATE_LAYOUT':
    case 'STAGE_RUN':
    case 'STAGE_RUN_SAMPLE':
      primitive = null;
      break;
    case 'STUDIES':
      primitive = typedValue.studies.map((study) => (shouldUseIds ? study.id : study.humanId));
      if (primitive.length === 1) {
        primitive = primitive[0];
      }
      break;
    case 'PROCEDURE':
      primitive = typedValue.procedures.map((procedure) => procedure.id);
      if (primitive.length === 1) {
        primitive = primitive[0];
      }
      break;
    case 'PROCEDURE_SCHEMA_FIELD_TYPE':
      primitive = typedValue.displayName;
      break;
    case 'PROCEDURE_CONDITION_REPLICATES':
      primitive = typedValue.conditionReplicates.map(
        ({procedureMethodExecutionInstanceId, conditionName, replicateNumber}) =>
          shouldUseIds
            ? procedureMethodExecutionInstanceId
            : toConditionReplicateString(conditionName, replicateNumber)
      );
      if (primitive.length === 1) {
        primitive = primitive[0];
      }
      break;
    case 'UNIT_REFERENCE':
      primitive = shouldUseIds ? typedValue.unitSummary.id : typedValue.unitSummary.symbol;
      break;
    case 'TEST_DEFINITION':
      primitive = typedValue.name;
      break;
    case 'LIBRARY_PARAMETER_DEFINITION':
      primitive = typedValue.name;
      break;
    case 'ERROR':
      primitive = 'ERROR';
      break;
    default:
      primitive = null;
  }

  if (shouldCoerceStringToNumber && typeof primitive === 'string') {
    const parsedValue = ExcessUtil.parseValue(primitive);
    if (!isBaseBoolean(parsedValue)) {
      primitive = parsedValue.rawValue;
    }
  }

  return primitive === undefined ? null : primitive;
}

/** Gets the numeric value from a typed value, or returns null. */
export function coerceToPrimitiveNumber(typedValue: TypedValue | null): number | null {
  if (typedValue == null) {
    return null;
  }
  const primitive = coerceToPrimitive(typedValue, {
    shouldCoerceStringToNumber: true,
    shouldCoerceDateToNumber: true,
  });
  if (typeof primitive === 'number') {
    return primitive;
  }
  return null;
}

/** Gets the boolean value from a TypedValue, or returns null.
 *
 * Notably, this does not simplify truthy/falsy values, only actual boolean values.
 */
export function coerceToPrimitiveBoolean(typedValue: TypedValue | null): boolean | null {
  if (typedValue == null) {
    return null;
  }
  const primitive = coerceToPrimitive(typedValue);
  if (typeof primitive === 'boolean') {
    return primitive;
  }
  return null;
}

/**
 * Returns a comparable primitive string for an ft_float typed value. Keep in sync with
 * float_typed_value_as_serialized_float_or_none in benchling/notebook/tables/typed_value_utils.py.
 *
 * @param tv the ft_float typed value.
 * @returns a comparable string representation of the typed value.
 */
function floatFieldTypedValueToComparablePrimitive(tv: FloatSchemaFieldTypedValue): string | null {
  if (tv.rawValue != null) {
    return tv.rawValue;
  }
  if (tv.value != null && tv.floatValue != null && parseFloat(tv.value) === tv.floatValue) {
    return tv.value;
  }
  if (tv.floatValue != null) {
    return tv.floatValue.toString();
  }
  return null;
}

/**
 * Float schema field value equivalence is determined by strictly comparing the user's input, taking into account
 * notation and precision in the input string.
 *
 * @param a ft_float typed value
 * @param b ft_float typed value
 * @returns true if the typed values are equivalent
 */
function areFloatFieldTypedValuesEquivalent(
  a: FloatSchemaFieldTypedValue,
  b: FloatSchemaFieldTypedValue
): boolean {
  return floatFieldTypedValueToComparablePrimitive(a) === floatFieldTypedValueToComparablePrimitive(b);
}

/**
 * Perform equivalence check for non-empty typed values. Allows custom equality comparisons depending on
 * typed value type.
 * Keep in sync with benchling/notebook/tables/typed_value_utils.py:are_typed_values_equivalent
 */
function checkNonEmptyTypedValueEquivalence(a: TypedValue | null, b: TypedValue | null): boolean {
  // For blob_link types, we should use the targetBlob ids for equivalence checking
  const shouldCompareIds = a?.type === FieldType.BLOB_LINK && b?.type === FieldType.BLOB_LINK;

  if (a?.type === FieldType.FLOAT && b?.type === FieldType.FLOAT) {
    return areFloatFieldTypedValuesEquivalent(a, b);
  }

  const primitiveA =
    a == null ? '' : (coerceToPrimitive(flattenValue(a), {shouldUseIds: shouldCompareIds}) ?? '');
  const primitiveB =
    b == null ? '' : (coerceToPrimitive(flattenValue(b), {shouldUseIds: shouldCompareIds}) ?? '');

  if (a?.type === BaseTypedValueType.PARTIES && b?.type === BaseTypedValueType.PARTIES) {
    return areSetsEqual(new Set(primitiveA as Array<string>), new Set(primitiveB as Array<string>));
  }

  return isEqual(primitiveA, primitiveB);
}

/**
 * Return whether a and b are equivalent (treating an empty string, null TypedValue, and null TypedValue.value
 * as equivalent).
 * Keep in sync with benchling/notebook/tables/typed_value_utils.py:are_typed_values_equivalent
 */
export function areTypedValuesEquivalent(a: TypedValue | null, b: TypedValue | null): boolean {
  return (
    checkNonEmptyTypedValueEquivalence(a, b) ||
    // `isEqual` returns False for empty typed values of different types, so we check this case individually
    (isTypedValueEmpty(a ?? toStringTypedValue('')) && isTypedValueEmpty(b ?? toStringTypedValue('')))
  );
}

interface LegacyCell extends Cell {
  blobLinks?: Array<BenchlingBlob>;
  datetimeValue?: string;
  schemaFieldSelectorOptionIds?: Array<string>;
}

/**
 * Convert a legacy table cell to use typedValue.
 */
export function convertLegacyCell(cellValue: LegacyCell, schemaFieldType: string | null = null): Cell {
  if (cellValue.typedValue) {
    return cellValue;
  }
  const {blobLinks, itemLinks, datetimeValue, schemaFieldSelectorOptionIds, ...newCellValue} = cellValue;
  if (schemaFieldType) {
    switch (schemaFieldType) {
      case FIELD_TYPES.BLOB_LINK.type:
        if (!cellValue.blobLinks) {
          return cellValue;
        }
        return {
          ...newCellValue,
          typedValue: {
            type: FieldType.BLOB_LINK,
            value: cellValue.blobLinks.map((link) => link.name).join(', '),
            targetBlobs: cellValue.blobLinks,
          },
        };
      case FIELD_TYPES.BATCH_LINK.type:
        if (!cellValue.itemLinks) {
          return cellValue;
        }
        return {
          ...newCellValue,
          typedValue: {
            type: 'ft_batch_link',
            value: null,
            targetBatches: cellValue.itemLinks as Array<BatchInfo>,
          },
        };
      case FIELD_TYPES.BIOENTITY_LINK.type:
        if (!cellValue.itemLinks) {
          return cellValue;
        }
        return {
          ...newCellValue,
          typedValue: {
            type: 'ft_file_link',
            value: null,
            targetFiles: cellValue.itemLinks as Array<TypedValueTargetFileNullishId>,
          },
        };
      case FIELD_TYPES.ENTRY_LINK.type:
        if (!cellValue.itemLinks) {
          return cellValue;
        }
        return {
          ...newCellValue,
          typedValue: {
            type: 'ft_entry_link',
            value: null,
            targetEntries: cellValue.itemLinks as Array<TypedValueTargetEntry>,
          },
        };
      case FIELD_TYPES.STORABLE_LINK.type:
        if (!cellValue.itemLinks) {
          return cellValue;
        }
        return {
          ...newCellValue,
          typedValue: {
            type: 'ft_storable_link',
            value: null,
            targetStorables: cellValue.itemLinks as Array<TypedValueTargetStorable>,
          },
        };
      case FIELD_TYPES.DATETIME.type:
        if (!cellValue.datetimeValue) {
          return cellValue;
        }
        return {
          ...newCellValue,
          typedValue: {
            type: FieldType.DATETIME,
            value: cellValue.datetimeValue,
            datetimeValue: cellValue.datetimeValue,
          },
        };
      case FIELD_TYPES.SELECTOR.type:
        if (!cellValue.schemaFieldSelectorOptionIds) {
          return cellValue;
        }
        return {
          ...newCellValue,
          typedValue: {
            type: FieldType.SELECTOR,
            schemaFieldSelectorOptionIds: cellValue.schemaFieldSelectorOptionIds,
          },
        };
      default:
        return cellValue;
    }
  }
  // No typed value
  return cellValue;
}

interface IntermediateDatetimeValue {
  type: BaseTypedValueType.DATETIME;
  rawValue: moment.Moment | null;
}

export const VALUE_COERCION_ERROR_MESSAGE = 'Value cannot be coerced.';
export const ARRAY_COERCION_HINT =
  'Use the CONCATENATE function on multi-value cells for comparison operations.';

/**
 * Given a typed value from a cell, convert it to an Excess type, particularly handling arrays of length 1
 */
export function coerceToSingleValue(
  typedValue: TypedValue
): StringTypedValue | NumberTypedValue | ScientificTypedValue | IntermediateDatetimeValue {
  if (typedValue.type === BaseTypedValueType.ARRAY) {
    if (typedValue.rawValue.length !== 1) {
      throw new ExcessUtil.ValueError(`${VALUE_COERCION_ERROR_MESSAGE} ${ARRAY_COERCION_HINT}`);
    }
    typedValue = coerceToSingleValue(typedValue.rawValue[0]) as
      | StringTypedValue
      | NumberTypedValue
      | ScientificTypedValue;
  }
  if (Object.values(ExcessUtil.NUMERIC_TYPES as Array<string>).includes(typedValue.type)) {
    return typedValue as ScientificTypedValue | NumberTypedValue;
  }
  if (
    [FieldType.DATETIME, FieldType.DATE, BaseTypedValueType.DATETIME, BaseTypedValueType.DATE].includes(
      typedValue.type as TypedValueType
    )
  ) {
    let value = null;
    // eslint-disable-next-line benchlint/no-in-operator
    if ('datetimeValue' in typedValue && typedValue.datetimeValue) {
      value = typedValue.datetimeValue;
      // eslint-disable-next-line benchlint/no-in-operator
    } else if ('dateValue' in typedValue && typedValue.dateValue) {
      value = typedValue.dateValue;
      // eslint-disable-next-line benchlint/no-in-operator
    } else if ('value' in typedValue) {
      value = typedValue.value;
    }
    return {
      type: BaseTypedValueType.DATETIME,
      rawValue: value ? DatetimeFields.tryParseAsMoment(value) : null,
    };
  }
  if (typedValue.type === FieldType.SELECTOR) {
    return {type: BaseTypedValueType.STRING, rawValue: typedValue.value || ''};
  }
  // This is for any type that isn't already a NUMBER/SCIENTIFIC, i.e. STRING or any of our tag schema values.
  const rawValue = coerceToPrimitive(typedValue, {shouldCoerceStringToNumber: true});
  if (typeof rawValue === 'number') {
    return {type: BaseTypedValueType.NUMBER, rawValue};
  } else if (typeof rawValue === 'string') {
    return {type: BaseTypedValueType.STRING, rawValue};
  } else if (typeof rawValue === 'boolean') {
    return {type: BaseTypedValueType.NUMBER, rawValue: rawValue ? 1 : 0};
  } else if (rawValue == null) {
    return {type: BaseTypedValueType.STRING, rawValue: ''};
  } else {
    if (Array.isArray(rawValue)) {
      throw new ExcessUtil.ValueError(`${VALUE_COERCION_ERROR_MESSAGE} ${ARRAY_COERCION_HINT}`);
    }
    throw new ExcessUtil.ValueError(VALUE_COERCION_ERROR_MESSAGE);
  }
}

/**
 * Given a typed value, return the list of linked items info, which may be in a different property
 * depending on the type.
 *
 * NOTE: Edits to this function must be reflected in
 * benchling/notebook/structured_tables/export/typed_value.py:get_links_from_typed_value.
 */
export function getLinksFromTypedValue(
  typedValue: TypedValue
): Array<AnyTypedValueLinkedObject | null> | null {
  const linksKey = getLinksKeyFromTypedValue(typedValue);
  if (linksKey != null && typedValue.hasOwnProperty(linksKey)) {
    // Cast is okay because we just confirmed the linksKey property exists,
    // and we union all possible property types in AnyTypedValueLinkedObject
    const castedTV = typedValue as TypedValue & {[key: string]: Array<AnyTypedValueLinkedObject>};
    if (!Array.isArray(castedTV[linksKey])) {
      assertAndContinue(false, `Expected ${linksKey} to be an array, but it was not.`);
    }
    return castedTV[linksKey];
  }
  return null;
}

/**
 * Given a typed value, return the name of the property that contains the list of linked objects info.
 *
 * If you add a case to this function:
 *  1. Make sure the property is stored as an array.
 *  2. Make sure the subtype of the array is included in the AnyTypedValueLinkedObject union.
 */
export function getLinksKeyFromTypedValue(
  typedValue: TypedValue
):
  | 'targetAssayRequests'
  | 'targetResults'
  | 'targetAssayRuns'
  | 'targetBatches'
  | 'targetBlobs'
  | 'targetEntries'
  | 'targetFiles'
  | 'targetStorables'
  | 'targetSamples'
  | 'storableSummaries'
  | 'partySummaries'
  | 'storablePlaceholders'
  | null {
  const linksKey = getLinksKeyFromFieldType(typedValue.type as FieldType);
  if (linksKey != null) {
    return linksKey;
  }
  switch (typedValue.type) {
    case BaseTypedValueType.STORABLES:
      return 'storableSummaries';

    case BaseTypedValueType.PARTIES:
      return 'partySummaries';

    case BaseTypedValueType.STORABLE_PLACEHOLDERS:
      return 'storablePlaceholders';

    default:
      return null;
  }
}

/**
 * Retrieves the ID of a non-array TypedValue used to render a smart chip in a cell.
 * Expand as needed.
 */
export function getChipIdFromTypedValue(typedValue: TypedValue): string | null {
  switch (typedValue.type) {
    case BaseTypedValueType.BIOENTITY:
      return typedValue.bioentitySummary?.id;

    default:
      return null;
  }
}

/**
 * Retrieves a list of IDs from a link typedValue. If the typed value type has no links, null is returned.
 * Refer to getLinksKeyFromTypedValue for accepted types.
 * Null items and null IDs are removed.
 */
export function getIdsFromTypedValue(typedValue: TypedValue): Array<string> | null {
  const links = getLinksFromTypedValue(typedValue);
  if (!links) {
    return null;
  }
  return links
    .filter((item) => item != null)
    .map((item) => item.id)
    .filter((id) => id != null);
}

interface FlattenSelectorValueOptions {
  /** When true, flattening keeps any duplicate selector values. By default, removes duplicates. */
  preserveDuplicates?: boolean;
}

function flattenSelectorValue(
  typedValue: ArrayTypedValue,
  options: FlattenSelectorValueOptions = {}
): SelectorSchemaFieldTypedValue | ArrayTypedValue {
  const {preserveDuplicates} = options;

  const flattenedOptionIds: Array<string> = [];
  const flattenedOptionNames = [];
  let areNamesValid = true;

  for (const singleRawValue of typedValue.rawValue as Array<SelectorSchemaFieldTypedValue>) {
    if (singleRawValue == null) {
      continue;
    }
    if (singleRawValue.schemaFieldSelectorOptionIds == null) {
      // This should never happen, but we are worried about legacy data. We can
      // remove this check if we don't see this warning for a while
      logging.warn('schemaFieldSelectorOptionIds is expected in selector typed value.', {
        extra: {singleRawValue, typedValue},
      });
      return typedValue;
    }
    const rawValueOptionIds = singleRawValue.schemaFieldSelectorOptionIds;

    if (rawValueOptionIds.length > 1) {
      const rawValueOptionNames = singleRawValue.value == null ? [] : singleRawValue.value.split(', ');

      if (rawValueOptionNames.length !== rawValueOptionIds.length) {
        logging.warn('Number of ids and names should match', {extra: {singleRawValue, typedValue}});
        // This may be caused by legacy data. We are not going to fill value at all rather than having only
        // some names present.
        areNamesValid = false;
      }

      for (let i = 0; i < rawValueOptionIds.length; i++) {
        if (!preserveDuplicates && flattenedOptionIds.includes(rawValueOptionIds[i])) {
          // Already included the option in the result
          continue;
        }
        flattenedOptionIds.push(rawValueOptionIds[i]);
        if (rawValueOptionNames.length === rawValueOptionIds.length) {
          flattenedOptionNames.push(rawValueOptionNames[i]);
        }
      }
    } else if (preserveDuplicates || !flattenedOptionIds.includes(rawValueOptionIds[0])) {
      flattenedOptionIds.push(rawValueOptionIds[0]);
      flattenedOptionNames.push(singleRawValue.value);
    }
  }
  return {
    type: FieldType.SELECTOR,
    schemaFieldSelectorOptionIds: flattenedOptionIds,
    value: areNamesValid ? flattenedOptionNames.join(', ') : '',
  };
}

export interface FlattenValueOptions extends FlattenSelectorValueOptions {}

/**
 * Flattens an array of length 1 or an array of the same-typed links
 */
export function flattenValue(typedValue: TypedValue, options: FlattenValueOptions = {}): TypedValue {
  if (typedValue.type !== BaseTypedValueType.ARRAY) {
    return typedValue;
  }
  if (typedValue.rawValue.length === 0) {
    return typedValue;
  }
  if (typedValue.rawValue.length === 1) {
    return flattenValue(typedValue.rawValue[0]);
  }
  const type = typedValue.rawValue[0].type;
  if (!typedValue.rawValue.every((value) => value.type === type)) {
    return typedValue;
  }
  if (type === FIELD_TYPES.SELECTOR.type) {
    const flattenSelectorValueOptions: FlattenSelectorValueOptions = pick(options, 'preserveDuplicates');
    return flattenSelectorValue(typedValue, flattenSelectorValueOptions);
  }
  const linksKey = getLinksKeyFromTypedValue(typedValue.rawValue[0]);
  if (!linksKey) {
    return typedValue;
  }
  let value;
  const links = _.chain(typedValue.rawValue)
    .pluck(linksKey)
    .compact()
    .flatten(true /* shallow */)
    .uniq(false /* isSorted */, (link) => link.id)
    .value();
  if (FIELD_TYPES.ASSAY_RUN_LINK.type === type) {
    value = links.map((link) => link.id).join(', ');
  } else {
    value = links.map((link) => link.name).join(', ');
  }
  return {type, value, [linksKey]: links} as TypedValue;
}

/**
 * NOTE: Edits to this function must be reflected in
 * benchling/notebook/structured_tables/export/typed_value.py:get_fallback_value.
 */
function getFallbackValue(typedValue: TypedValue): string {
  // eslint-disable-next-line benchlint/no-in-operator
  return 'value' in typedValue && typedValue.value ? typedValue.value : '';
}

export function getCellPrecision(
  cell: Optional<Partial<Cell>>,
  defaultPrecision: Optional<number>
): number | null {
  // Compute precision based off of implicit precision as well as the explicit precision set on cell
  let precision = null;
  if (cell && cell.precision != null) {
    precision = cell.precision;
  } else if (defaultPrecision != null) {
    precision = defaultPrecision;
  }
  if (precision != null) {
    precision = clamp(ExcessUtil.MIN_PRECISION, ExcessUtil.MAX_PRECISION, precision);
  }
  return precision;
}

export function getScientificNotation(cell: Optional<Partial<Cell>>): boolean | null {
  if (cell && cell.scientificNotation != null) {
    return cell.scientificNotation;
  }
  return null;
}

/**
 * Return string representation of a number or scientific typed value.
 *
 * NOTE: Edits to this function must be reflected in
 * benchling/notebook/structured_tables/export/typed_value.py:format_excess_numeric_typed_value.
 */
export function formatExcessNumericTypedValue(
  typedValue: ScientificTypedValue | NumberTypedValue,
  precision: Optional<number>,
  cellScientificNotation: Optional<boolean>
): string {
  if (typedValue.rawValue == null) {
    return '';
  }

  // If the scientific notation toggle is set on the cell and conflicts with the type of the numeric typed
  // value, format the value according to the toggle, ignoring the defaultPrecision value set on the value.
  // This is necessary because the parsed precision does not translate 1:1 when converting between the two
  // display formats. In the future, we might want to consider translating defaultPrecision based on the
  // magnitude of the value, but the desired behavior is not obvious for very large or very small values.
  if (cellScientificNotation === true && typedValue.type === ExcessUtil.VALUE_TYPES.NUMBER) {
    return typedValue.rawValue.toExponential(precision || undefined);
  } else if (cellScientificNotation === false && typedValue.type === ExcessUtil.VALUE_TYPES.SCIENTIFIC) {
    return prettyFloat(typedValue.rawValue, precision);
  }
  if (precision == null && typedValue.defaultPrecision != null) {
    precision = clamp(ExcessUtil.MIN_PRECISION, ExcessUtil.MAX_PRECISION, typedValue.defaultPrecision);
  }

  const scientificNotationValue = typedValue.rawValue.toExponential(precision || undefined);

  switch (typedValue.type) {
    case ExcessUtil.VALUE_TYPES.SCIENTIFIC:
      return scientificNotationValue;
    case ExcessUtil.VALUE_TYPES.NUMBER: {
      // If the abs(value) to format is non-zero but < DISPLAY_AS_SCIENTIFIC_THRESHOLD, we display it in scientific notation
      // This is necessary because prettyFloat formats using fixed precision, which will drop small
      // decimal values in a manner that users may not expect.
      // We only do this if the precision is not set, which implies that the typed value is a  result of
      // formula evaluation rather than a directly entered value, and also that precision has not been
      // explicitly set on the cell.
      if (precision == null && shouldDefaultToScientificNotation(typedValue.rawValue)) {
        return scientificNotationValue;
      }

      return prettyFloat(typedValue.rawValue, precision);
    }
    default: {
      // TypeScript infers never here since all cases are covered, but we still want runtime error info
      const value = typedValue as ScientificTypedValue | NumberTypedValue;
      throw new Error(`Expected a numeric type but got ${value.type}`);
    }
  }
}

/**
 * Return true if a decimal value should be formatted to scientific notation for clarity.
 *
 * NOTE: Edits to this function must be reflected in
 * benchling/notebook/structured_tables/export/typed_value.py:should_default_to_scientific_notation.
 */
function shouldDefaultToScientificNotation(value: number): boolean {
  const absValue = Math.abs(value);
  return absValue !== 0 && absValue < ExcessUtil.DISPLAY_AS_SCIENTIFIC_THRESHOLD;
}

/**
 * Converts a scientific or decimal typed value to a float schema field typed value.
 *
 * If defaultPrecision is specified on the input, this indicates that the value was input directly and is not
 * the result of formula evaluation. In this case, we use the common formatting function, albeit ignoring
 * cell-level precision and notation settings.
 *
 * If defaultPrecision is not set, the input is the result of a formula and we format to the maximum fidelity
 * possible.
 */
function resolveBaseNumericTypedValueToFloatSchemaTypedValue(
  typedValue: ScientificTypedValue | NumberTypedValue,
  schemaDecimalPrecision?: number | null
): {type: FieldType.FLOAT; value: string; rawValue: string; floatValue: number} {
  let rawValue: string;
  if (typedValue.defaultPrecision != null) {
    rawValue = formatExcessNumericTypedValue(typedValue, null, null);
  } else if (
    typedValue.type === ExcessUtil.VALUE_TYPES.NUMBER &&
    !shouldDefaultToScientificNotation(typedValue.rawValue)
  ) {
    rawValue = stripTrailingZeros(typedValue.rawValue.toPrecision(15));
  } else {
    rawValue = stripTrailingZeros(typedValue.rawValue.toExponential(15));
  }

  const floatValue = parseFloat(rawValue);
  const value = schemaDecimalPrecision != null ? floatValue.toFixed(schemaDecimalPrecision) : rawValue;

  return {
    type: FieldType.FLOAT,
    rawValue,
    value,
    floatValue,
  };
}

function stripTrailingZeros(formattedNumber: string): string {
  if (formattedNumber.indexOf('.') < 0) {
    return formattedNumber;
  }
  return formattedNumber.replace(/(\d{1})\.?0+([eE][+-]?\d+)?$/g, '$1$2');
}

/**
 * Format a string or numeric typed value.
 *
 * NOTE: Edits to this function must be reflected in
 * benchling/notebook/structured_tables/export/typed_value.py:format_primitive_typed_value.
 */
function formatPrimitiveTypedValue(
  typedValue:
    | TextSchemaFieldTypedValue
    | LongTextSchemaFieldTypedValue
    | FloatSchemaFieldTypedValue
    | IntegerSchemaFieldTypedValue
): string {
  const text = coerceToPrimitive(typedValue) || getFallbackValue(typedValue);
  return String(text);
}

/**
 * Returns a string desdcription of a lookup step.
 *
 * NOTE: Edits to this function must be reflected in
 * benchling/notebook/structured_tables/export/lookup_config_typed_value.py:lookup_step_description.
 */
function lookupStepDescription(
  lookupStep: LookupStepSpec,
  sourceLookupConfig?: LookupConfigSpec | null,
  destinationLookupConfig?: LookupConfigSpec | null
): string {
  switch (lookupStep.type) {
    case LookupStepType.ALIASES:
      return 'Aliases';
    case LookupStepType.AUTHORIZED_USERS:
      return getRestrictedSampleUsersHeaderText();
    case LookupStepType.AVAILABLE_SPACE:
      return 'Available space';
    case LookupStepType.BARCODE:
      return 'Barcode';
    case LookupStepType.BOX:
      return 'Box';
    case LookupStepType.BOX_CONTAINERS:
      return 'Box Containers';
    case LookupStepType.CHEMICAL_STRUCTURE:
      return 'Chemical Structure';
    case LookupStepType.CHILD_ENTITIES:
      return 'Child Entities';
    case LookupStepType.COMPUTED_EXPIRATION_DATE:
      return 'Expiration date';
    case LookupStepType.FREEZE_THAW_COUNT:
      return 'Freeze/thaw count';
    case LookupStepType.CONCENTRATION:
      return 'Concentration';
    case LookupStepType.CONSTANT:
      return lookupStep.value.toString();
    case LookupStepType.CONTAINER:
      return 'Container';
    case LookupStepType.CONTENTS:
      return 'Contents';
    case LookupStepType.COUNT:
      return `Count (${lookupStep.schemaField})`;
    case LookupStepType.DESTINATION:
      return destinationLookupConfig != null
        ? getLookupConfigDescription(destinationLookupConfig)
        : 'Destination';
    case LookupStepType.FILTER:
      return `Filter (${lookupStep.schemaField})`;
    case LookupStepType.LATEST:
      return `Most recent item created`;
    case LookupStepType.LOCATION:
      return 'Location';
    case LookupStepType.LOCATION_PATH:
      return 'Location path';
    case LookupStepType.NAME:
      return 'Name';
    case LookupStepType.NOTEBOOK_COLUMN_TYPECHECK:
      return 'Notebook column';
    case LookupStepType.PLACEHOLDER_BOXES:
      return 'Placeholder Boxes';
    case LookupStepType.PLACEHOLDER_PLATES:
      return 'Placeholder Plates';
    case LookupStepType.PLATE:
      return 'Plate';
    case LookupStepType.FLOWCHART_PROCESS_TASK:
      return 'Flowchart task';
    case LookupStepType.PROCESS_TASK:
    case LookupStepType.TASK_FINDER_PROCESS_TASKS:
      return 'Task';
    case LookupStepType.PROCESS_TASK_DEFINITION_FIELD:
      return lookupStep.processTaskDefinitionFieldIdentifier || 'Missing field';
    case LookupStepType.REGISTRY_IDENTIFIER:
      return 'Registry ID';
    case LookupStepType.REPLICATES:
      return 'Replicates';
    case LookupStepType.RESIDUES:
      return 'Residues';
    case LookupStepType.RESULTS:
      return 'Results';
    case LookupStepType.SCHEMA_FIELD:
      return lookupStep.schemaField || 'Missing field';
    case LookupStepType.SAMPLE_OWNERS:
      return 'Sample owners';
    case LookupStepType.SCHEMA_IDENTITY:
      return 'Schema';
    case LookupStepType.SOURCE:
      return sourceLookupConfig != null ? getLookupConfigDescription(sourceLookupConfig) : 'Source';
    case LookupStepType.QUANTITY:
      return getQuantityDisplayString();
    case LookupStepType.WELL_COORDINATES:
      return 'Position';
    case LookupStepType.WELLS:
      return 'Wells';
    case LookupStepType.FILTER_BY_ORIGIN:
      return 'Filter by Origin';
    case LookupStepType.CONTAINER_BY_GRID_POSITION:
      return 'Well/container by position in box or plate';
    case LookupStepType.STUDIES:
      return 'Studies';
    default:
      return '';
  }
}

/**
 * Returns a string description of a lookup config.
 *
 * NOTE: Edits to this function must be reflected in
 * benchling/notebook/structured_tables/export/lookup_config_typed_value.py:get_lookup_config_description.
 */
export function getLookupConfigDescription(
  lookupConfig: LookupConfigSpec,
  sourceLookupConfig?: LookupConfigSpec | null,
  destinationLookupConfig?: LookupConfigSpec | null
): string {
  return lookupConfig.lookupSteps
    .map((lookupStep: LookupStepSpec) =>
      lookupStepDescription(lookupStep, sourceLookupConfig, destinationLookupConfig)
    )
    .join(` ${RIGHT_ARROW} `);
}

/**
 * Return string representation of the typed value
 *
 * NOTE: Edits to this function must be reflected in
 * benchling/notebook/structured_tables/export/typed_value.py:format_typed_value_to_csv.
 */
export function formatTypedValue(
  typedValue: TypedValue,
  cell?: Partial<Cell>,
  separator: string = ', ',
  inaccessibleIds?: Set<string>
): string {
  const {type} = typedValue;
  switch (type) {
    case BaseTypedValueType.ARRAY:
      return typedValue.rawValue
        .map((val) => formatTypedValue(val, cell, separator, inaccessibleIds))
        .join(separator);
    case FieldType.ASSAY_RESULT_LINK:
    case FieldType.ASSAY_RUN_LINK: {
      const links = getLinksFromTypedValue(typedValue) as Array<TypedValueLinkedValueWithNullishId>;
      if (!links) {
        return getFallbackValue(typedValue);
      }
      return links.map((link) => link.id).join(separator);
    }
    case BaseTypedValueType.BATCH:
      return typedValue.batchInfo.name;
    case FieldType.AA_PART_LINK:
    case FieldType.BATCH_LINK:
    case FieldType.BIOENTITY_LINK:
    case FieldType.BLOB_LINK:
    case FieldType.ENTRY_LINK:
    case FieldType.PART_LINK:
    case FieldType.STORABLE_LINK:
    case FieldType.TRANSCRIPTION_LINK:
    case FieldType.TRANSLATION_LINK: {
      const links = getLinksFromTypedValue(typedValue) as Array<TypedValueTargetFileGuaranteedId>;
      if (!links) {
        return getFallbackValue(typedValue);
      }
      return links
        .map((link) => (inaccessibleIds && inaccessibleIds.has(link.id) ? '(No access)' : link.name))
        .join(separator);
    }
    case BaseTypedValueType.BIOENTITY:
      return inaccessibleIds && inaccessibleIds.has(typedValue.bioentitySummary.id)
        ? '(No access)'
        : typedValue.bioentitySummary.name;
    case BaseTypedValueType.BOOLEAN:
      return typedValue.booleanValue ? 'TRUE' : 'FALSE';
    case FieldType.BOOLEAN:
      if (typedValue.booleanValue == null) {
        return getFallbackValue(typedValue);
      }
      return String(typedValue.booleanValue);
    case BaseTypedValueType.CLIENT_IDENTIFIABLE:
      return typedValue.name;
    case BaseTypedValueType.COMPUTING:
      return COMPUTING_STATUS_TEXT;
    case BaseTypedValueType.DATE:
    case FieldType.DATE: {
      // eslint-disable-next-line benchlint/no-in-operator
      if (!('dateValue' in typedValue) || !typedValue.dateValue) {
        return getFallbackValue(typedValue);
      }
      return String(typedValue.dateValue);
    }
    case BaseTypedValueType.DATETIME:
    case FieldType.DATETIME:
      // eslint-disable-next-line benchlint/no-in-operator
      if (!('datetimeValue' in typedValue) || !typedValue.datetimeValue) {
        return getFallbackValue(typedValue);
      }
      return formalTime(typedValue.datetimeValue, /* includeTimezone */ true);
    case BaseTypedValueType.ENTRY:
      return typedValue.name;
    case FieldType.FLOAT:
    case FieldType.INTEGER: {
      if (!typedValue.value) {
        return formatPrimitiveTypedValue(typedValue);
      }

      // Hack to format the cell like a regular number/scientific value.
      // typedValue.floatValue doesn't have enough information, so we parse the full text value
      // using ExcessUtil.parseValue.
      const parsedTypedValue = ExcessUtil.parseValue(typedValue.value);
      if (!isBaseNumericValueType(parsedTypedValue)) {
        // Cell value is not a number
        return getFallbackValue(typedValue);
      }
      const cellPrecision = getCellPrecision(cell, null);
      const scientificNotation = getScientificNotation(cell);
      return formatExcessNumericTypedValue(parsedTypedValue, cellPrecision, scientificNotation);
    }
    case BaseTypedValueType.FULFILLABLE_STATUS:
      return typedValue.status ? FULFILLMENT_STATUS_NAMES[typedValue.status] : '';
    case BaseTypedValueType.GRID_COORDINATES:
      return formatGridCoordinates(
        typedValue.gridCoordinates.rowIndex,
        typedValue.gridCoordinates.columnIndex
      );
    case BaseTypedValueType.JSON:
    case FieldType.JSON:
      if (typedValue.jsonValue == null) {
        return getFallbackValue(typedValue);
      }
      // Equivalent to json.dumps() in Python (no whitespace)
      return JSON.stringify(typedValue.jsonValue);
    case BaseTypedValueType.LOOKUP_CONFIG_INFO:
      return typedValue.lookupConfig != null
        ? getLookupConfigDescription(
            typedValue.lookupConfig as LookupConfigSpec,
            typedValue.source as LookupConfigSpec,
            typedValue.destination != null ? (typedValue.destination as LookupConfigSpec) : null
          )
        : '';
    case BaseTypedValueType.NUMBER:
    case BaseTypedValueType.SCIENTIFIC: {
      const cellPrecision = getCellPrecision(cell, null);
      const scientificNotation = getScientificNotation(cell);
      return formatExcessNumericTypedValue(typedValue, cellPrecision, scientificNotation);
    }
    case BaseTypedValueType.PARTIES:
      return typedValue.partySummaries.map((party) => party.name).join(separator);
    case BaseTypedValueType.PLATE_LAYOUT:
      return typedValue.plateLayout != null
        ? readablePlateLayout(typedValue.plateLayout as PlateLayoutType)
        : 'None';
    case BaseTypedValueType.PROCESS_TASK:
    case BaseTypedValueType.PROCESS_OUTPUT:
      return typedValue.humanId;
    case BaseTypedValueType.PROCEDURE:
      return typedValue.procedures.map((procedure) => procedure.id).join(separator);
    case BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE:
      return typedValue.displayName;
    case BaseTypedValueType.PROCEDURE_CONDITION_REPLICATES:
      return typedValue.conditionReplicates
        .map(({conditionName, replicateNumber}) => toConditionReplicateString(conditionName, replicateNumber))
        .join(separator);
    case BaseTypedValueType.SAMPLE_RESTRICTION_STATUS:
      return SAMPLE_RESTRICTION_STATUS_LABELS[typedValue.status];
    case FieldType.SELECTOR:
      return getFallbackValue(typedValue);
    case BaseTypedValueType.STAGE_RUN:
      return typedValue.stageRunName;
    case BaseTypedValueType.STAGE_RUN_SAMPLE:
      return typedValue.stageRunSample ? typedValue.stageRunSample.name : '';
    case BaseTypedValueType.STORABLES:
      return typedValue.storableSummaries.map((storable) => storable.name).join(separator);
    case BaseTypedValueType.STORABLE_PLACEHOLDERS:
      return convertStorablePlaceholdersTypedValueToString(typedValue);
    case BaseTypedValueType.STRING:
      return String(typedValue.rawValue);
    case BaseTypedValueType.STUDIES:
      return typedValue.studies.map((study) => study.humanId).join(separator);
    case BaseTypedValueType.TASK_STATUS:
      return typedValue.displayName;
    case BaseTypedValueType.TEST_DEFINITION:
      return typedValue.name;
    case BaseTypedValueType.LIBRARY_PARAMETER_DEFINITION:
      return typedValue.name;
    case FieldType.TEXT:
    case FieldType.LONG_TEXT:
      return formatPrimitiveTypedValue(typedValue);
    case BaseTypedValueType.UNIT_REFERENCE:
      return typedValue.unitSummary.symbol;
    case BaseTypedValueType.USER:
      return typedValue.name ?? typedValue.userHandle;
    default:
      logging.warn('Unexpected type for formatTypedValue', {extra: {typedValue}});
      return getFallbackValue(typedValue);
  }
}

/**
 * Return string representation of the typed value
 *
 * TODO(BNCH-127648): Delete benchling/notebook/structured_tables/action_functions/create_dataset.py::_create_data_frame_from_evaluated_rows
 * and unify paths once we have added the extra registry column for bioentities.
 */
export function formatTypedValueWithIds(
  typedValue: TypedValue,
  cell?: Partial<Cell>,
  separator: string = ', ',
  inaccessibleIds?: Set<string>
): string {
  const {type} = typedValue;
  switch (type) {
    case BaseTypedValueType.ARRAY:
      return typedValue.rawValue
        .map((val) => formatTypedValueWithIds(val, cell, separator, inaccessibleIds))
        .join(separator);
    case FieldType.ASSAY_RESULT_LINK:
    case FieldType.ASSAY_RUN_LINK: {
      const links = getLinksFromTypedValue(typedValue) as Array<TypedValueLinkedValueWithNullishId>;
      if (!links) {
        return getFallbackValue(typedValue);
      }
      return links.map((link) => link.id).join(separator);
    }
    case BaseTypedValueType.BATCH:
      return typedValue.batchInfo.id ?? typedValue.batchInfo.name;
    case FieldType.AA_PART_LINK:
    case FieldType.BATCH_LINK:
    case FieldType.BIOENTITY_LINK:
    case FieldType.BLOB_LINK:
    case FieldType.ENTRY_LINK:
    case FieldType.PART_LINK:
    case FieldType.STORABLE_LINK:
    case FieldType.TRANSCRIPTION_LINK:
    case FieldType.TRANSLATION_LINK: {
      const links = getLinksFromTypedValue(typedValue) as Array<TypedValueTargetFileGuaranteedId>;
      if (!links) {
        return getFallbackValue(typedValue);
      }
      return links
        .map((link) => (inaccessibleIds && inaccessibleIds.has(link.id) ? '(No access)' : link.id)) // TODO(DENISE): confirm
        .join(separator);
    }
    case BaseTypedValueType.BIOENTITY:
      return inaccessibleIds && inaccessibleIds.has(typedValue.bioentitySummary.id)
        ? '(No access)'
        : typedValue.bioentitySummary.id;
    case BaseTypedValueType.BOOLEAN:
      return typedValue.booleanValue ? 'TRUE' : 'FALSE';
    case FieldType.BOOLEAN:
      if (typedValue.booleanValue == null) {
        return getFallbackValue(typedValue);
      }
      return String(typedValue.booleanValue);
    case BaseTypedValueType.CLIENT_IDENTIFIABLE:
      return typedValue.id;
    case BaseTypedValueType.COMPUTING:
      return COMPUTING_STATUS_TEXT;
    case BaseTypedValueType.DATE:
    case FieldType.DATE: {
      // eslint-disable-next-line benchlint/no-in-operator
      if (!('dateValue' in typedValue) || !typedValue.dateValue) {
        return getFallbackValue(typedValue);
      }
      return String(typedValue.dateValue);
    }
    case BaseTypedValueType.DATETIME:
    case FieldType.DATETIME:
      // eslint-disable-next-line benchlint/no-in-operator
      if (!('datetimeValue' in typedValue) || !typedValue.datetimeValue) {
        return getFallbackValue(typedValue);
      }
      return formalTime(typedValue.datetimeValue, /* includeTimezone */ true);
    case BaseTypedValueType.ENTRY:
      return typedValue.id;
    case FieldType.FLOAT:
    case FieldType.INTEGER: {
      if (!typedValue.value) {
        return formatPrimitiveTypedValue(typedValue);
      }

      // Hack to format the cell like a regular number/scientific value.
      // typedValue.floatValue doesn't have enough information, so we parse the full text value
      // using ExcessUtil.parseValue.
      const parsedTypedValue = ExcessUtil.parseValue(typedValue.value);
      if (!isBaseNumericValueType(parsedTypedValue)) {
        // Cell value is not a number
        return getFallbackValue(typedValue);
      }
      const cellPrecision = getCellPrecision(cell, null);
      const scientificNotation = getScientificNotation(cell);
      return formatExcessNumericTypedValue(parsedTypedValue, cellPrecision, scientificNotation);
    }
    case BaseTypedValueType.FULFILLABLE_STATUS:
      return typedValue.status ? FULFILLMENT_STATUS_NAMES[typedValue.status] : '';
    case BaseTypedValueType.GRID_COORDINATES:
      return formatGridCoordinates(
        typedValue.gridCoordinates.rowIndex,
        typedValue.gridCoordinates.columnIndex
      );
    case BaseTypedValueType.JSON:
    case FieldType.JSON:
      if (typedValue.jsonValue == null) {
        return getFallbackValue(typedValue);
      }
      // Equivalent to json.dumps() in Python (no whitespace)
      return JSON.stringify(typedValue.jsonValue);
    case BaseTypedValueType.LOOKUP_CONFIG_INFO:
      return typedValue.lookupConfig != null
        ? getLookupConfigDescription(
            typedValue.lookupConfig as LookupConfigSpec,
            typedValue.source as LookupConfigSpec,
            typedValue.destination != null ? (typedValue.destination as LookupConfigSpec) : null
          )
        : '';
    case BaseTypedValueType.NUMBER:
    case BaseTypedValueType.SCIENTIFIC: {
      const cellPrecision = getCellPrecision(cell, null);
      const scientificNotation = getScientificNotation(cell);
      return formatExcessNumericTypedValue(typedValue, cellPrecision, scientificNotation);
    }
    case BaseTypedValueType.PARTIES:
      return typedValue.partySummaries.map((party) => party.id).join(separator);
    case BaseTypedValueType.PLATE_LAYOUT:
      return typedValue.plateLayout != null
        ? readablePlateLayout(typedValue.plateLayout as PlateLayoutType)
        : 'None';
    case BaseTypedValueType.PROCESS_TASK:
    case BaseTypedValueType.PROCESS_OUTPUT:
      return typedValue.id;
    case BaseTypedValueType.PROCEDURE:
      return typedValue.procedures.map((procedure) => procedure.id).join(separator);
    case BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE:
      return typedValue.displayName;
    case BaseTypedValueType.PROCEDURE_CONDITION_REPLICATES:
      return typedValue.conditionReplicates
        .map(({procedureMethodExecutionInstanceId}) => procedureMethodExecutionInstanceId)
        .join(separator);
    case BaseTypedValueType.SAMPLE_RESTRICTION_STATUS:
      return SAMPLE_RESTRICTION_STATUS_LABELS[typedValue.status];
    case FieldType.SELECTOR:
      return getFallbackValue(typedValue);
    case BaseTypedValueType.STAGE_RUN:
      // TODO(BNCH-127648): Consider change management for making this id
      return typedValue.stageRunName;
    case BaseTypedValueType.STAGE_RUN_SAMPLE:
      // TODO(BNCH-127648): Consider change management for making this id
      return typedValue.stageRunSample ? typedValue.stageRunSample.name : '';
    case BaseTypedValueType.STORABLES:
      return typedValue.storableSummaries.map((storable) => storable.id).join(separator);
    case BaseTypedValueType.STORABLE_PLACEHOLDERS:
      return convertStorablePlaceholdersTypedValueToString(typedValue);
    case BaseTypedValueType.STRING:
      return String(typedValue.rawValue);
    case BaseTypedValueType.STUDIES:
      // TODO(BNCH-127648): Consider change management for making this id
      return typedValue.studies.map((study) => study.name).join(separator);
    case BaseTypedValueType.TASK_STATUS:
      return typedValue.displayName;
    case BaseTypedValueType.TEST_DEFINITION:
      return typedValue.id ?? typedValue.name;
    case BaseTypedValueType.LIBRARY_PARAMETER_DEFINITION:
      return typedValue.id;
    case FieldType.TEXT:
    case FieldType.LONG_TEXT:
      return formatPrimitiveTypedValue(typedValue);
    case BaseTypedValueType.UNIT_REFERENCE:
      return typedValue.unitSummary.id;
    case BaseTypedValueType.USER:
      return typedValue.id;
    default:
      logging.warn('Unexpected type for formatTypedValue', {extra: {typedValue}});
      return getFallbackValue(typedValue);
  }
}

export function isTypedValueEmpty(typedValue: TypedValue): boolean {
  // TODO(BNCH-3625): can remove once we migrate old dropdown values
  if (typedValue.type === FieldType.SELECTOR) {
    return (
      typedValue.schemaFieldSelectorOptionIds == null || typedValue.schemaFieldSelectorOptionIds.length === 0
    );
  }
  if (typedValue.type === BaseTypedValueType.LOOKUP_CONFIG_INFO) {
    // never treat lookup config typed values as empty because they contain
    // source and destination information even while empty
    return false;
  }
  return formatTypedValue(typedValue).trim() === '';
}

export function getTypedValueFromBioentitySummary(bioentitySummary: null): null;

export function getTypedValueFromBioentitySummary(bioentitySummary: {
  id?: string | null;
  displayLabel?: string;
}): BioentityTypedValue;

export function getTypedValueFromBioentitySummary(
  bioentitySummary: {id?: string | null; displayLabel?: string} | null
): BioentityTypedValue | null {
  if (!(bioentitySummary && bioentitySummary.id && bioentitySummary.displayLabel)) {
    return null;
  }
  return {
    type: BaseTypedValueType.BIOENTITY,
    bioentitySummary: {id: bioentitySummary.id, name: bioentitySummary.displayLabel},
  };
}

export function toBioentityTypedValueCell(bioentitySummary: {id: string; displayLabel: string} | null): Cell {
  const typedValue =
    bioentitySummary == null
      ? getTypedValueFromBioentitySummary(bioentitySummary)
      : getTypedValueFromBioentitySummary(bioentitySummary);
  return {text: '', typedValue};
}

export function getTypedValueFromStorableSummaries(
  // Usually expects a storable summary, but sometimes a full summary may be unavailable
  // so we can just use id and name to extract typed value
  storableSummaries: Array<{id: string; name: string}>
): StorablesTypedValue {
  return {
    type: BaseTypedValueType.STORABLES,
    storableSummaries: storableSummaries.map(({id, name}) => ({id, name})),
  };
}

export function toStorableTypedValueCell(storableSummaries: Array<{id: string; name: string}>): Cell {
  return {text: '', typedValue: getTypedValueFromStorableSummaries(storableSummaries)};
}

// TODO(BNCH-57795) Fully handle BenchlingApps instead of just grouping them as users
export function getTypedValueFromPartySummaries(
  partySummaries: Array<{
    id: string;
    name: string;
    handle: string;
    type?: ModelTypeConstants.USER | ModelTypeConstants.TEAM | ModelTypeConstants.BENCHLING_APP;
    __typename?: 'UserNode' | 'TeamNode' | 'BenchlingAppNode';
    partyType?: 'TEAM' | 'USER';
  }>
): PartiesTypedValue {
  assert(
    partySummaries.every(
      ({type, __typename, partyType}) => type != null || __typename != null || partyType != null
    ),
    'Expected either type, __typename, or partyType to be provided for every party'
  );
  return {
    type: BaseTypedValueType.PARTIES,
    partySummaries: partySummaries.map(({id, name, handle, type, __typename, partyType}) => ({
      id,
      name,
      handle,
      partyType:
        (partyType ?? (__typename === 'TeamNode' || type === ModelTypeConstants.TEAM || APIUtil.isTeam(id)))
          ? 'TEAM'
          : 'USER',
    })),
  };
}

export function getTypedValueFromSampleRestrictionStatus(
  restrictionStatus: SampleRestrictionStatus
): SampleRestrictionStatusTypedValue {
  return {type: BaseTypedValueType.SAMPLE_RESTRICTION_STATUS, status: restrictionStatus};
}

export function getTypedValueFromLinkedBioentities(
  bioentitySummaries: Array<BioentitySummary>,
  value: string | null
): BioentityLinkSchemaFieldTypedValue {
  return {
    type: FieldType.BIOENTITY_LINK,
    value,
    targetFiles: bioentitySummaries.map(({id, name, editURL}) => ({id, name, url: editURL})),
  };
}

/**
 * Combines the target files of the given bioentity link schema field typed values into a single typed value.
 * Does not deduplicate target files.
 */
export function flattenBioentityLinkSchemaFieldTypedValues(
  typedValues: Array<BioentityLinkSchemaFieldTypedValue>,
  value?: string
): BioentityLinkSchemaFieldTypedValue {
  return {
    type: FieldType.BIOENTITY_LINK,
    value: value ?? '',
    targetFiles: typedValues.flatMap((typedValue) => typedValue.targetFiles),
  };
}

export function getUserTypedValueFromEntitySummary(entitySummary: EntitySummary): UserTypedValue {
  return {
    type: BaseTypedValueType.USER,
    id: entitySummary.id,
    avatarUrl: entitySummary.avatar_url,
    userHandle: entitySummary.handle,
    name: entitySummary.name,
  };
}

export function toStringTypedValue(text: string): StringTypedValue {
  return {type: 'STRING', rawValue: text};
}

export function toStringTypedValueCell(text: string | null): Cell {
  if (text == null) {
    return {text: ''};
  } else {
    return {text: '', typedValue: toStringTypedValue(text)};
  }
}

export function getBioentityInfoFromTypedValue(
  typedValue: TypedValue | null | undefined
): BioentityInfo | null {
  if (typedValue == null || typedValue.type !== BaseTypedValueType.BIOENTITY) {
    return null;
  }
  return typedValue.bioentitySummary;
}

export function getBioentitySummaryFromTypedValue(
  typedValue: TypedValue
): BioentitySummary | SequenceSummary | null {
  if (!typedValue || typedValue.type !== BaseTypedValueType.BIOENTITY) {
    return null;
  }
  return getStore().getState().models.fileSummariesById[typedValue.bioentitySummary.id];
}

export function getBioentityInfosFromTypedValue(
  typedValue: TypedValue | null | undefined
): Array<BioentityInfo> | null {
  if (
    typedValue == null ||
    (typedValue.type !== BaseTypedValueType.ARRAY && typedValue.type !== BaseTypedValueType.BIOENTITY)
  ) {
    return null;
  }
  if (typedValue.type === BaseTypedValueType.BIOENTITY) {
    return [typedValue.bioentitySummary];
  }
  const allBioentities = typedValue.rawValue.every((val) => val.type === BaseTypedValueType.BIOENTITY);
  if (!allBioentities || !typedValue.rawValue.length) {
    return null;
  }
  return (typedValue.rawValue as Array<BioentityTypedValue>).map((val) => val.bioentitySummary);
}

export function getBioentityTargetFileIdsFromTypedValue(typedValue: TypedValue): Array<string | null> | null {
  if (typedValue.type !== FieldType.BIOENTITY_LINK) {
    return null;
  }
  // Assuming that this method isn't called on deleted entities.
  return typedValue.targetFiles.map((targetFile) => targetFile.id);
}

export function getBatchInfoFromTypedValue(typedValue: TypedValue): BatchInfo | null {
  if (!typedValue || typedValue.type !== BaseTypedValueType.BATCH) {
    return null;
  }
  return typedValue.batchInfo;
}

export function getStorableInfosFromTypedValue(typedValue: TypedValue | null): Array<StorableInfo> {
  if (!typedValue || typedValue.type !== BaseTypedValueType.STORABLES) {
    return [];
  }
  return typedValue.storableSummaries;
}

export function getStorableIdsFromTypedValue(typedValue: TypedValue | null): Array<string> {
  if (!typedValue || typedValue.type !== BaseTypedValueType.STORABLES) {
    return [];
  }
  return typedValue.storableSummaries.map(({id}) => id);
}

export function getStorableInfoFromTypedValue(typedValue: TypedValue): StorableInfo | null {
  const storableInfos = getStorableInfosFromTypedValue(typedValue);
  if (storableInfos.length === 0) {
    return null;
  }
  assertAndContinue(
    storableInfos.length === 1,
    `"getStorableInfoFromTypedValue" was called on a cell with multiple storables.
    You should use "getStorableInfosFromTypedValue" instead.`,
    undefined,
    {fingerprint: ['benchling', 'data-input', 'typed-value', 'storable-info-from-typed-value']}
  );
  return storableInfos[0];
}

export function getBooleanCellValue(cell: Cell): boolean {
  if (cell.typedValue && cell.typedValue.type === BaseTypedValueType.BOOLEAN) {
    return cell.typedValue.booleanValue;
  }
  return false;
}

export function getProcessTaskIdFromTypedValue(typedValue: TypedValue): string | null {
  const taskId =
    typedValue.type === BaseTypedValueType.PROCESS_TASK ? typedValue.id : formatTypedValue(typedValue);
  return taskId && APIUtil.isProcessTask(taskId) ? taskId : null;
}

export function getProcessTaskIdFromCell(cell: Cell): string | null {
  let taskId;
  if (cell.typedValue?.type === BaseTypedValueType.PROCESS_TASK) {
    taskId = cell.typedValue.id;
  } else if (cell.typedValue) {
    taskId = formatTypedValue(cell.typedValue);
  } else {
    // TODO(BNCH-27720): We no longer need to inspect cell.text
    taskId = cell.text;
  }
  return taskId && APIUtil.apiIdToMaybeType(taskId) === ModelTypeConstants.PROCESS_TASK ? taskId : null;
}

export function getProcessOutputIdFromTypedValue(typedValue?: TypedValue | null): string | null {
  return typedValue?.type === BaseTypedValueType.PROCESS_OUTPUT ? typedValue.id : null;
}

export function getTaskStatusFromCell(cell: Cell): TaskStatusTypedValue | null {
  return cell.typedValue?.type === BaseTypedValueType.TASK_STATUS ? cell.typedValue : null;
}

export function getPartyInfosFromTypedValue(typedValue: TypedValue | null): Array<PartyInfo> {
  if (!typedValue || typedValue.type !== BaseTypedValueType.PARTIES) {
    return [];
  }
  return typedValue.partySummaries;
}

export function getPartyIdsFromTypedValue(typedValue: TypedValue | null): Array<string> {
  if (!typedValue || typedValue.type !== BaseTypedValueType.PARTIES) {
    return [];
  }
  return typedValue.partySummaries.map(({id}) => id);
}

export function sanitizeTypedValueForSave(
  typedValue: TypedValue | null | undefined
): TypedValue | null | undefined {
  if (!typedValue) {
    return typedValue;
  }
  const {type} = typedValue;
  switch (type) {
    case 'ARRAY':
    case 'BATCH':
    case 'BIOENTITY':
    case 'BOOLEAN':
    case 'CLIENT_IDENTIFIABLE':
    case 'COMPUTING':
    case 'DATE':
    case 'DATETIME':
    case 'ENTRY':
    case 'FULFILLABLE_STATUS':
    case 'GRID_COORDINATES':
    case 'JSON':
    case 'LOOKUP_CONFIG_INFO':
    case 'NUMBER':
    case 'PARTIES':
    case 'PLATE_LAYOUT':
    case 'PROCEDURE':
    case 'PROCEDURE_SCHEMA_FIELD_TYPE':
    case 'PROCEDURE_CONDITION_REPLICATES':
    case 'SAMPLE_RESTRICTION_STATUS':
    case 'SCIENTIFIC':
    case 'STAGE_RUN':
    case 'STAGE_RUN_SAMPLE':
    case 'STRING':
    case 'STORABLES':
    case 'STORABLE_PLACEHOLDERS':
    case 'STUDIES':
    case 'TASK_STATUS':
    case 'TEST_DEFINITION':
    case 'LIBRARY_PARAMETER_DEFINITION':
    case 'UNIT_REFERENCE':
    case 'USER':
    case 'WORKFLOW_OUTPUT':
    case 'WORKFLOW_TASK':
    case BaseTypedValueType.ARRAY:
    case 'ft_assay_result_link':
    case 'ft_assay_request_link':
    case 'ft_assay_run_link':
    case 'ft_batch_link':
      return typedValue;
    case 'ft_blob_link':
      return {
        ...typedValue,
        targetBlobs: sanitizeBenchlingBlobs(
          (typedValue.targetBlobs ?? []).map((blob) => convertTypedValueBlob(blob))
        ),
      };
    case 'ft_aa_part_link':
    case 'ft_boolean':
    case 'ft_date':
    case 'ft_datetime':
    case 'ft_entry_link':
    case 'ft_file_link':
    case 'ft_float':
    case 'ft_integer':
    case 'ft_json':
    case 'ft_long_text':
    case 'ft_part_link':
    case 'ft_selector':
    case 'ft_storable_link':
    case 'ft_string':
    case 'ft_transcription_link':
    case 'ft_translation_link':
      return typedValue;
    case 'ERROR':
      assert(false, 'ERROR typed values should never be persisted and therefore should never be sanitized');
      break;
    default:
      assertNever(type);
      return typedValue;
  }
}

export function createFloatTypedValue(value: number | null): FloatSchemaFieldTypedValue {
  const stringRepresentation = value != null ? String(value) : null;
  return {
    type: FieldType.FLOAT,
    value: stringRepresentation,
    rawValue: stringRepresentation,
    floatValue: value,
  };
}
